/**
 * 岩手県の背景ツアー「岩手めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 中尊寺金色堂は奥州藤原氏4代の遺体を納めた墓堂で、堂内に仏像が安置されている。
 *   撮影の可否も公式サイトで確認できなかったため、入れていない。
 * - 焼走り熔岩流は「特別天然記念物」ではない。「天然記念物」と書く。
 * - 北山崎の格付けは「全国観光資源評価 自然資源 海岸・岬の部で最高ランクの特A級」という
 *   限定つきの表現をそのまま使う。
 * - 猊鼻渓の舟は事業者の持ち物なので、意匠を特定できる形では描かない。
 * - 厳美渓の名物だんごは民間の店のサービスなので描かない。
 * - 盛岡城跡は石垣だけを描く。園内の石碑や銅像は確認していない。
 * - カッパ淵のカッパ像、小岩井農場の一本桜は権利関係のため入れていない。
 * - 高田松原は国土交通省と岩手県が「追悼」「鎮魂」「犠牲者」と明記している祈りの場なので、
 *   solemn を立てて静かに描く。一本松の高さや松原の本数は公式で確認できなかったので書かない。
 */

import type { StageTour } from '../types';

export const iwateSceneryTour: StageTour = {
  scenes: [
    {
      id: 'dragon-eye',
      name: '八幡平 ドラゴンアイ',
      area: '八幡平市',
      image: '/assets/scenery/iwate/scenes/01_dragon_eye.webp',
      hotspots: [
        {
          id: 'dragon-eye-season',
          x: 0.28,
          y: 0.3,
          title: '残雪期だけのコース',
          body: '環境省が紹介するドラゴンアイをめぐるコースは、5月上旬から6月上旬の残雪期だけの限定コースになっている。',
          source: 'https://www.env.go.jp/nature/nationalparks/try/all/act1579.html',
          mapQuery: '八幡平 鏡沼',
        },
        {
          id: 'dragon-eye-altitude',
          x: 0.6,
          y: 0.6,
          title: '標高約1600メートル',
          body: '八幡平の山頂部、標高およそ1600メートルにある鏡沼。山頂付近には雪解けの湖沼群が点在する。',
          source: 'https://www.env.go.jp/nature/nationalparks/try/all/act1579.html',
          mapQuery: '八幡平山頂',
        },
        {
          id: 'dragon-eye-contrast',
          x: 0.8,
          y: 0.35,
          title: '白と青のコントラスト',
          body: '環境省は「残雪の白と沼の青のコントラスト」「宝石のような雪解けの景色」と紹介している。',
          source: 'https://www.env.go.jp/nature/nationalparks/try/all/act1579.html',
          mapQuery: '八幡平 ドラゴンアイ',
        },
      ],
    },
    {
      id: 'motsuji',
      name: '毛越寺 浄土庭園',
      area: '平泉町',
      image: '/assets/scenery/iwate/scenes/02_motsuji.webp',
      hotspots: [
        {
          id: 'motsuji-double',
          x: 0.25,
          y: 0.4,
          title: '特別史跡と特別名勝の二重指定',
          body: '毛越寺は特別史跡と特別名勝の二重の指定を受けており、さらに世界遺産に登録されている。',
          source: 'https://www.motsuji.or.jp/keidai/',
          mapQuery: '毛越寺',
        },
        {
          id: 'motsuji-garden',
          x: 0.55,
          y: 0.7,
          title: '『作庭記』の思想を伝える',
          body: '浄土庭園は仏堂と苑池が一体として配された庭園で、大泉が池を中心に、州浜、荒磯風の水分けの石組み、浪返しにあたる立石、枯山水風の築山、池に水を引き入れる遣水が置かれている。日本最古の作庭書『作庭記』の思想を伝える。',
          source: 'https://www.motsuji.or.jp/keidai/',
          mapQuery: '毛越寺 大泉が池',
        },
        {
          id: 'motsuji-unesco',
          x: 0.8,
          y: 0.3,
          title: '世界遺産委員会の評価',
          body: '世界遺産委員会は毛越寺庭園について「浄土思想の理想と、庭園・水・周辺景観の結びつきに関する日本古来の概念との融合を例証している」と評価した。境内には全盛期の伽藍遺跡が良好な状態で残る。',
          source: 'https://www.town.hiraizumi.iwate.jp/heritage/asset/motsuji.html',
          mapQuery: '平泉 毛越寺',
        },
      ],
    },
    {
      id: 'jodogahama',
      name: '浄土ヶ浜',
      area: '宮古市',
      image: '/assets/scenery/iwate/scenes/03_jodogahama.webp',
      hotspots: [
        {
          id: 'jodogahama-name',
          x: 0.25,
          y: 0.35,
          title: '「さながら極楽浄土のごとし」',
          body: '天和年間（1681〜1683年）に宮古山常安寺七世の霊鏡竜湖が「さながら極楽浄土のごとし」と感嘆したことから名づけられた。',
          source: 'https://www.city.miyako.iwate.jp/gyosei/soshiki/kanko/4/8/2/1/1154.html',
          mapQuery: '浄土ヶ浜',
        },
        {
          id: 'jodogahama-rock',
          x: 0.55,
          y: 0.55,
          title: '白いのは流紋岩だから',
          body: '4400万年前にマグマの働きでできた流紋岩という火山岩で、二酸化ケイ素を多く含むため白い。流理構造や節理が観察できる。',
          source: 'https://www.city.miyako.iwate.jp/gyosei/soshiki/kanko/4/8/2/1/1154.html',
          mapQuery: '浄土ヶ浜 宮古市',
        },
        {
          id: 'jodogahama-designation',
          x: 0.8,
          y: 0.75,
          title: '岩手県指定名勝の第1号',
          body: '昭和29年に岩手県指定名勝の第1号となり、昭和30年に陸中海岸国立公園、平成24年に国指定名勝、平成25年に三陸復興国立公園となった。',
          source: 'https://www.city.miyako.iwate.jp/gyosei/soshiki/kanko/4/8/2/1/1154.html',
          mapQuery: '浄土ヶ浜ビジターセンター',
        },
      ],
    },
    {
      id: 'kitayamazaki',
      name: '北山崎',
      area: '田野畑村',
      image: '/assets/scenery/iwate/scenes/04_kitayamazaki.webp',
      hotspots: [
        {
          id: 'kitayamazaki-height',
          x: 0.3,
          y: 0.4,
          title: '高さ200メートル、約8キロ',
          body: '高さ200メートルの断崖に、ふしぎな形をした大小さまざまな岩や洞窟がある。ダイナミックな海岸線が約8キロメートルにわたって続く。',
          source: 'https://www.vill.tanohata.iwate.jp/kankou/see/kitayamazaki.html',
          mapQuery: '北山崎',
        },
        {
          id: 'kitayamazaki-rank',
          x: 0.6,
          y: 0.28,
          title: '海岸・岬の部で特A級',
          body: '日本交通公社による全国観光資源評価の「自然資源 海岸・岬の部」で、最高ランクの特A級に格付けされた景勝地。',
          source: 'https://www.vill.tanohata.iwate.jp/kankou/see/kitayamazaki.html',
          mapQuery: '北山崎展望台',
        },
        {
          id: 'kitayamazaki-stairs',
          x: 0.5,
          y: 0.8,
          title: '736段を下りると',
          body: '展望台は第1から第3までの3つ。第1展望台からの階段736段を最後まで下りれば、海面と同じ高さから断崖を見上げることもできる。',
          source: 'https://www.vill.tanohata.iwate.jp/kankou/see/kitayamazaki.html',
          mapQuery: '北山崎 第一展望台',
        },
      ],
    },
    {
      id: 'goishi-kaigan',
      name: '碁石海岸 穴通磯',
      area: '大船渡市',
      image: '/assets/scenery/iwate/scenes/05_goishi.webp',
      hotspots: [
        {
          id: 'goishi-kaigan-name',
          x: 0.25,
          y: 0.8,
          title: '名前は黒い玉砂利から',
          body: '海岸の東南にある「碁石浜」に、波で磨かれた黒い玉砂利が敷き詰められていることが名前の由来だという説がある。',
          source: 'https://www.city.ofunato.iwate.jp/site/ofunatrip/9173.html',
          mapQuery: '碁石海岸',
        },
        {
          id: 'goishi-kaigan-holes',
          x: 0.55,
          y: 0.45,
          title: '三つの穴',
          body: '穴通磯は海水の浸食によって岩に大きな穴が3つ開いている。小型の漁船で穴をくぐり抜けることもできる。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/sanriku-fukko/spot/',
          mapQuery: '穴通磯',
        },
        {
          id: 'goishi-kaigan-eight',
          x: 0.78,
          y: 0.3,
          title: '碁石八景',
          body: '国の名勝・天然記念物で、日本の渚百選、日本の白砂青松百選にも選ばれている。碁石八景として碁石浜、穴通磯、雷岩、乱曝谷、碁石岬、えびす浜、千代島、赤土倉が挙げられ、海岸線は約6キロ続く。',
          source: 'https://www.city.ofunato.iwate.jp/site/ofunatrip/9173.html',
          mapQuery: '碁石海岸 大船渡市',
        },
      ],
    },
    {
      id: 'geibikei',
      name: '猊鼻渓',
      area: '一関市',
      image: '/assets/scenery/iwate/scenes/06_geibikei.webp',
      hotspots: [
        {
          id: 'geibikei-meisho',
          x: 0.25,
          y: 0.35,
          title: '1925年に名勝',
          body: '猊鼻渓は大正14年（1925年）10月8日に名勝に指定された。所在地は岩手県一関市竹山町。',
          source: 'https://online.bunka.go.jp/heritages/detail/190022',
          mapQuery: '猊鼻渓',
        },
        {
          id: 'geibikei-water',
          x: 0.55,
          y: 0.7,
          title: '魚が数えられるほど澄む',
          body: '指定の解説は「水蝕ニ因レル石灰岩ノ渓谷」とし、岩のほとりは流れがゆるやかに曲がり、水面は鏡のように澄んで泳ぐ魚が数えられるほどだと記している。',
          source: 'https://online.bunka.go.jp/heritages/detail/190022',
          mapQuery: '猊鼻渓 一関市',
        },
        {
          id: 'geibikei-length',
          x: 0.78,
          y: 0.5,
          title: '約二十町',
          body: '指定の範囲は「約二十町ノ間」と書かれている。砂鉄川をはさんで岩壁が向かい合い、鍾乳洞や滝もあると記されている。',
          source: 'https://online.bunka.go.jp/heritages/detail/190022',
          mapQuery: '猊鼻渓 舟下り',
        },
      ],
    },
    {
      id: 'genbikei',
      name: '厳美渓',
      area: '一関市',
      image: '/assets/scenery/iwate/scenes/07_genbikei.webp',
      hotspots: [
        {
          id: 'genbikei-meisho',
          x: 0.25,
          y: 0.3,
          title: '1927年に名勝',
          body: '厳美渓は昭和2年（1927年）9月5日に名勝に指定された。所在地は岩手県一関市厳美町。',
          source: 'https://online.bunka.go.jp/heritages/detail/137594',
          mapQuery: '厳美渓',
        },
        {
          id: 'genbikei-rock',
          x: 0.55,
          y: 0.6,
          title: '粗大な柱状節理',
          body: '指定の解説は、磐井川上流の峡谷が「粗大ナル柱状節理ヲ呈セル斜長流紋岩ノ地域ニ穿タレタル」ものであり、瀑布があり急瀬があり深渕があり、また甌穴があると記す。',
          source: 'https://online.bunka.go.jp/heritages/detail/137594',
          mapQuery: '厳美渓 一関市',
        },
        {
          id: 'genbikei-phrase',
          x: 0.8,
          y: 0.78,
          title: '「水ハ清ク石ハ奇ニ」',
          body: '同じ解説は「水ハ清ク石ハ奇ニ水石ノ変化尋常山水ノ比ニ非ス」と結んでいる。ふつうの山水の比ではない、という意味。',
          source: 'https://online.bunka.go.jp/heritages/detail/137594',
          mapQuery: '厳美渓 甌穴',
        },
      ],
    },
    {
      id: 'ryusendo',
      name: '龍泉洞',
      area: '岩泉町',
      image: '/assets/scenery/iwate/scenes/08_ryusendo.webp',
      hotspots: [
        {
          id: 'ryusendo-tennen',
          x: 0.25,
          y: 0.3,
          title: 'コウモリとともに天然記念物',
          body: '龍泉洞は、洞内に棲むコウモリとともに国の天然記念物に指定されている鍾乳洞である。',
          source: 'https://www.iwate-ryusendo.jp/about/',
          mapQuery: '龍泉洞',
        },
        {
          id: 'ryusendo-length',
          x: 0.55,
          y: 0.75,
          title: '総延長4088メートル',
          body: '洞内の総延長は知られているところで4,088メートル。そのうち700メートルが公開されている。総延長は5,000メートル以上ではないかとも言われる。',
          source: 'https://www.iwate-ryusendo.jp/about/',
          mapQuery: '龍泉洞 岩泉町',
        },
        {
          id: 'ryusendo-lakes',
          x: 0.78,
          y: 0.5,
          title: '地底湖は8つ',
          body: '見つかっている地底湖は8つで、そのうち3つが公開されている。世界有数の透明度をもつ、目の覚めるような青い地底湖である。',
          source: 'https://www.iwate-ryusendo.jp/about/',
          mapQuery: '龍泉洞 地底湖',
        },
      ],
    },
    {
      id: 'yakehashiri',
      name: '焼走り熔岩流',
      area: '八幡平市',
      image: '/assets/scenery/iwate/scenes/09_yakehashiri.webp',
      hotspots: [
        {
          id: 'yakehashiri-eruption',
          x: 0.3,
          y: 0.65,
          title: '享保四年の噴火',
          body: '享保四年正月の岩手山の噴火の際、東側の谷口から噴き出した熔岩流で、長さは約4キロメートル、末端における幅は1.5キロメートルある。',
          source: 'https://online.bunka.go.jp/heritages/detail/200966',
          mapQuery: '焼走り熔岩流',
        },
        {
          id: 'yakehashiri-tennen',
          x: 0.6,
          y: 0.85,
          title: '天然記念物',
          body: '昭和19年（1944年）11月7日に天然記念物に指定された。輝石安山岩からなり、表面には波状の凹凸がある。噴出から200年以上たっても大部分に樹木が生えていない。',
          source: 'https://online.bunka.go.jp/heritages/detail/200966',
          mapQuery: '焼走り熔岩流 八幡平市',
        },
        {
          id: 'yakehashiri-iwatesan',
          x: 0.75,
          y: 0.25,
          title: '山体崩壊の回数は国内最多',
          body: '岩手山は標高2,038メートル、気象庁の常時観測火山。西岩手と東岩手という2つの成層火山からなり、少なくとも7回の大規模な山体崩壊が起きている。その回数は国内の活火山のなかで最も多い。',
          source: 'https://www.data.jma.go.jp/vois/data/sendai/207_Iwatesan/207_index.html',
          mapQuery: '岩手山',
        },
      ],
    },
    {
      id: 'takata-matsubara',
      name: '高田松原',
      area: '陸前高田市',
      image: '/assets/scenery/iwate/scenes/10_takata_matsubara.webp',
      solemn: true,
      hotspots: [
        {
          id: 'takata-matsubara-purpose',
          x: 0.3,
          y: 0.35,
          title: '追悼と鎮魂の場',
          body: '高田松原津波復興祈念公園は、犠牲者への追悼と鎮魂、震災の記憶と教訓の伝承、国内外に向けた復興への意志の発信の場として整えられた。「奇跡の一本松が残ったこの場所で、犠牲者への追悼と鎮魂の思いとともに、震災の教訓とそこからの復興の姿を高田松原の再生と重ね合わせ未来に伝えていく」ことが基本理念に掲げられている。',
          source: 'https://www.thr.mlit.go.jp/m-park/memorial_park/takada.html',
          mapQuery: '高田松原津波復興祈念公園',
        },
        {
          id: 'takata-matsubara-facilities',
          x: 0.6,
          y: 0.6,
          title: '公園のなかにあるもの',
          body: '公園内には国営追悼・祈念施設、道の駅「高田松原」、東日本大震災津波伝承館、そして奇跡の一本松をはじめとする震災遺構が置かれている。入場は無料で、公園のエリアは24時間開かれている。',
          source: 'https://www5.pref.iwate.jp/~hp0132/cgi-bin/facility.cgi?f1=1734075190',
          mapQuery: '東日本大震災津波伝承館',
        },
        {
          id: 'takata-matsubara-opening',
          x: 0.75,
          y: 0.8,
          title: '全面開園は令和3年',
          body: '令和元年9月22日に一部の利用が始まり、令和3年4月1日に開園、令和3年12月26日に全面開園した。「奇跡の一本松」はこの公園のなかにある。',
          source: 'https://www.thr.mlit.go.jp/m-park/memorial_park/takada.html',
          mapQuery: '奇跡の一本松',
        },
      ],
    },
  ],
};
